<template>
  <OverlayHeader
    :heading="identificationObjective?.config.commonName"
    :message="identificationObjective?.config.scientificName"
  />
  <OverlayMessage
    v-for="(message, index) in messages"
    :key="message.id"
    :id="message.id"
    :message="message.text"
    :index="index"
  />
  <div
    v-if="showEndGameMessage"
    ref="blocker"
    class="w-100 h-100 position-relative showAboveCanvas bg-black-opacity-50"
  >
    <div
      id="instructions"
      ref="instructions"
      class="
        text-white
        w-100
        h-100
        d-flex
        flex-column
        justify-content-center
        align-items-center
        showAboveCanvas
        cursorPointer
      "
    >
      <div class="alert bg-black-opacity-75 mt-2 cursorPointer">
        <div class="fs-2 text-uppercase">Congratulations!</div>
        <p class="fs-4 text-start font-weight-bold">
          Game Completed! You have found all of the birds.
        </p>
      </div>

      <h1 class="fs-1 btn btn-primary" @click="endGame">Back to Main Page</h1>
    </div>
  </div>
  <!-- Need to use a blocker overlay to lock/unlock pointer from window -->
  <div
    ref="blocker"
    v-if="isGamePaused()"
    @click="lockControls"
    class="w-100 h-100 position-relative showAboveCanvas bg-black-opacity-50"
  >
    <div
      id="instructions"
      ref="instructions"
      class="
        text-white
        w-100
        h-100
        d-flex
        flex-column
        justify-content-center
        align-items-center
        showAboveCanvas
        cursorPointer
      "
    >
      <div class="alert bg-black-opacity-75 mt-2">
        <h2>Instructions</h2>
        <p class="fs-4 text-start font-weight-bold mx-3">
          <span
            v-bind:key="instruction"
            v-for="instruction in content.pauseScreen.instructions"
          >
            {{ instruction }}<br />
          </span>
        </p>
      </div>
      <div class="alert bg-black-opacity-75 w-75">
        <div class="row">
          <div class="col">
            <img :src="keyboardInstructionsSVG" />
          </div>
        </div>
      </div>
      <div class="btn-group gap-2">
        <button class="fs-1 btn btn-primary px-3">
          {{ content.pauseScreen.title }}
        </button>
        <RouterLink
          :to="{ name: 'ReferenceData' }"
          class="fs-1 btn btn-primary px-3"
        >
          Home</RouterLink
        >
      </div>
    </div>
  </div>
  <div
    id="reticleContainer"
    v-if="photographyViewEnabled()"
    class="showAboveCanvas d-flex justify-content-around"
    :class="{ animateShutter: showShutterAnimation }"
  >
    <img
      id="reticleSVG"
      :src="require('@/assets/img/CameraReticle.svg')"
      alt="camera reticle"
    />
  </div>
  <!-- set canvas size to full window (top and bottom are covered by header and footer)-->
  <canvas
    id="canvas"
    ref="threejs-canvas"
    class="top-0 start-0 h-100 w-100 position-absolute"
  />
</template>

<script>
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import Stats from "three/examples/jsm/libs/stats.module.js";
import FirstPersonControls from "@/three/FirstPersonControls";
import Bird from "@/three/Bird";
import OverlayHeader from "@/components/OverlayHeader.vue";
import OverlayMessage from "@/components/OverlayMessage.vue";
import keyboardInstructionsSVG from "@/assets/controls/keyboard-controls.svg";

import forestJSON from "@/data/forest.json";
import wetlandJSON from "@/data/wetland.json";
import { LAYERS, WATER_SPEED } from "@/three/constants";
import {
  setLayer,
  isTree,
  isGrass,
  birdMapToArray
} from "@/helpers/ThreeHelpers";
import {
  setCastShadow,
  configureLightShadowSettings,
  configureRendererShadowSettings
} from "@/helpers/ThreeHelpers";
import { processTerrainModel } from "@/helpers/TerrainHelpers";
import {
  GAME_STATUS,
  RENDER_CONSTANTS,
  ZOOM_VALUES,
  BIRD_POSITION_TYPES,
  TEST_PLACEMENT,
  MIN_APPARENT_SIZE,
  MIN_APPARENT_SIZE_FLYING,
  MAX_APPARENT_SIZE
} from "@/helpers/GameConstants";

import content from "@/data/content.json";

import { Water } from "three/examples/jsm/objects/Water";
import { ENVIRONMENTS } from "@/helpers/GameConstants";
import { mapGetters } from "vuex";

const models = {};
let scene;
let camera, miniMapCamera;
// Dimensions for minimap
let insetWidth, insetHeight;
let controls;
let animationID;
let renderer;
let clock;
// Parts of the terrain that should affect the player
let colliders;
const birds = {};
// Names of birds that have been identified
const identifiedBirds = [];
let audioListener;

let shutterSound;
let water; // water shader
let stats;

export default {
  name: "GameScene",
  emits: ["modelsLoaded"],
  props: {
    environment: {
      type: String,
      required: true
    }
  },
  components: {
    OverlayHeader,
    OverlayMessage
  },
  data: function () {
    return {
      keyboardInstructionsSVG,
      // TODO: Figure out how to set the initial dimensions of the canvas and keep them fixed
      fixedDimensions: {
        width: 800,
        height: 600
      },
      gameStatus: GAME_STATUS.PAUSED,
      previousGameStatus: GAME_STATUS.DEFAULT,
      // Used to throttle controls locking
      canLockControls: true,
      // Indicates if user has interacted with the window at least once
      hasLockedControls: false,
      showShutterAnimation: false,
      identificationObjective: null,
      messages: [],
      content: content,
      showEndGameMessage: false
    };
  },
  mounted: function () {
    // Register Event Listeners
    window.addEventListener("resize", this.updateDimensions);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("click", this.handleClickEvent);

    this.init();
  },
  unmounted: function () {
    // dispose of listeners and animation loop
    window.removeEventListener("resize", this.updateDimensions);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("click", this.handleClickEvent);

    this.cleanUpThreeJS();
  },
  computed: {
    ...mapGetters(["isDebugMode"])
  },
  methods: {
    pauseAudio: function () {
      // Pause all bird audio
      birdMapToArray(birds).forEach((bird) => bird.pauseAudio());
    },
    resumeAudio: function () {
      // Resume all bird audio
      birdMapToArray(birds).forEach((bird) => bird.resumeAudio());
    },
    handleClickEvent(event) {
      if (event.button == 0) {
        this.clickToTakePhotoMethod(event);
      } else if (event.button == 2) {
        this.zoomMethod(event);
      }
    },
    addMessage: function (text) {
      const id = this.messages?.length ? this.messages[0].id + 1 : 0;
      console.log("this.messages", this.messages);
      console.log("ID in add message function", id);

      // Place new message at front of array
      this.messages.unshift({ id: id, text: text });
      // only show one message at a time
      if (this.messages.length > 1) {
        this.messages.pop();
      }
    },
    clickToTakePhotoMethod: function () {
      if (!this.photographyViewEnabled()) {
        return;
      }
      // Display shutter animation for .2 seconds
      this.showShutterAnimation = true;
      setTimeout(() => {
        this.showShutterAnimation = false;
      }, 200);
      shutterSound.play();

      this.gradePhoto();
    },
    // Grade photo based on apparent size of any instances of the current identification objective
    gradePhoto: function () {
      const minApparentSize =
        this.identificationObjective.config.flying == true
          ? MIN_APPARENT_SIZE_FLYING
          : MIN_APPARENT_SIZE;

      function getApparentSize(bird) {
        // calculate distances from objective
        const distance = camera.position.distanceTo(bird.model.position);
        const actualBirdSize = bird.size;
        // Calculate effective FOV based on camera zoom
        const zoomLevel = camera.zoom;
        const originalFOV = camera.fov; // in degrees
        const effectiveFOV = originalFOV / zoomLevel; // Adjusted FOV due to zoom
        // Calculate the height of the camera view at the bird's distance
        const viewHeightAtDistance =
          2 * distance * Math.tan(THREE.MathUtils.degToRad(effectiveFOV / 2));
        // Calculate the apparent size (as a ratio of the bird's size to the view height at that distance)
        const apparentSize = actualBirdSize / viewHeightAtDistance;
        return apparentSize;
      }

      // See if the objective is in view
      const frustum = new THREE.Frustum();
      const viewMatrix = new THREE.Matrix4().multiplyMatrices(
        camera.projectionMatrix,
        camera.matrixWorldInverse
      );
      frustum.setFromProjectionMatrix(viewMatrix);
      let birdsInView = [];
      const flatBirdsArray = Object.entries(birds).flatMap(
        ([birdName, birdArray]) =>
          birdArray.map((bird) => ({ ...bird, name: birdName }))
      );
      flatBirdsArray.forEach((bird) => {
        if (frustum.containsPoint(bird.model.position)) {
          if (
            bird.config.commonName !==
            this.identificationObjective.config.commonName
          ) {
            // if wrong bird is not over minimum size, do not register it as a wrong bird
            // We don't want distant, non-objective birds to pollute the grading message
            if (getApparentSize(bird) < minApparentSize) {
              return;
            }
          }
          // TODO: eliminate birds that are are blocked by other objects (ground in particular)
          birdsInView.push({ ...bird, apparentSize: getApparentSize(bird) });
        }
      });
      console.log("Length of birds in view: ", birdsInView.length);

      // If no grade-able birds are in view
      if (birdsInView.length === 0) {
        this.addMessage("No birds in view");
        return;
      }
      // If grade-able birds are in view
      else {
        const correctBirdsInView = birdsInView.filter(
          (bird) =>
            bird.config.commonName ===
            this.identificationObjective.config.commonName
        );
        // If correct bird is not amongst grade-able birds in view
        if (correctBirdsInView.length === 0) {
          this.addMessage("That is not the bird you're looking for");
          return;
        }
        let correctBirdsAboveThreshold = correctBirdsInView.filter(
          (bird) => bird.apparentSize > minApparentSize
        );
        // if correct bird is in view and is large enough
        if (correctBirdsAboveThreshold.length > 0) {
          // if correct bird not too large, message "Great shot!"
          if (
            correctBirdsAboveThreshold.some(
              (bird) => bird.apparentSize < MAX_APPARENT_SIZE
            )
          ) {
            this.addMessage("Great shot!");
            this.selectNewIdentificationObjective();
            return;
          } else {
            // if correct bird is in view but too large, message "Bird too close"
            this.addMessage("You're too close");
            return;
          }
        }
        // if correct bird is in view but too small, message "Bird too far"
        this.addMessage("Try to get closer");
      }
    },
    zoomMethod() {
      if (this.gameStatus !== GAME_STATUS.PAUSED) {
        if (this.gameStatus === GAME_STATUS.PHOTOGRAPHY) {
          this.gameStatus = GAME_STATUS.PHOTOGRAPHY_ZOOMED;
          camera.zoom = ZOOM_VALUES[GAME_STATUS.PHOTOGRAPHY_ZOOMED];
        } else if (this.gameStatus === GAME_STATUS.PHOTOGRAPHY_ZOOMED) {
          this.gameStatus = GAME_STATUS.DEFAULT;
          camera.zoom = ZOOM_VALUES[GAME_STATUS.DEFAULT];
        } else {
          this.gameStatus = GAME_STATUS.PHOTOGRAPHY;
          camera.zoom = ZOOM_VALUES[GAME_STATUS.PHOTOGRAPHY];
        }
        camera.updateProjectionMatrix();
      }
    },
    isGamePaused: function () {
      return this.gameStatus === GAME_STATUS.PAUSED;
    },
    photographyViewEnabled: function () {
      return this.gameStatus.includes("PHOTOGRAPHY");
    },
    lockControls: function () {
      if (this.canLockControls) {
        this.gameStatus = this.previousGameStatus;
        this.canLockControls = false;
        this.resumeAudio();
        // Start audio context after first user interaction has occurred
        if (!this.hasLockedControls) {
          // create an AudioListener and add it to the camera
          audioListener = new THREE.AudioListener();
          camera.add(audioListener);
          // TODO: only start birds that need to be active at the start of the game
          Object.keys(birds).forEach((birdName) => {
            birds[birdName].forEach((bird, index, array) =>
              bird.initAudio(audioListener, array.length, index)
            );
          });
          this.hasLockedControls = true;
        }
        controls.lock();
      }
    },
    unlockControls: function () {
      this.previousGameStatus = this.gameStatus;
      this.gameStatus = GAME_STATUS.PAUSED;
      this.pauseAudio();
      // https://discourse.threejs.org/t/how-to-avoid-pointerlockcontrols-error/33017/5
      setTimeout(() => {
        this.canLockControls = true;
      }, RENDER_CONSTANTS.LOCK_DEBOUNCE);
    },
    getModelPath(name, extension = "glb") {
      // I changed this to glb from gltf. I don't think it broke anything.
      return ` models/${name}.${extension}`;
    },
    initCameraControls: function (canvas) {
      // Set intended camera orientation and control speed
      controls = new FirstPersonControls(
        camera,
        canvas,
        colliders,
        this.lockControls,
        this.unlockControls,
        scene,
        false,
        new THREE.Vector3(0, 0, 0),
        new THREE.Euler(0, 0, 0)
      );

      scene.add(controls.getObject());
    },
    initCameras: function () {
      // First Person Camera
      camera = new THREE.PerspectiveCamera(
        75,
        window.clientWidth / window.clientHeight,
        0.5,
        2000000
      );
      // Hide objects intended for mini map only
      camera.layers.enableAll();
      camera.layers.disable(LAYERS.MINI_MAP_ONLY);
      // MiniMap Camera
      // Size to fit the terrain
      const cameraSize = 300;
      // Always square viewport
      const aspectRatio = 1;
      miniMapCamera = new THREE.OrthographicCamera(
        (-cameraSize * aspectRatio) / 2,
        (cameraSize * aspectRatio) / 2,
        cameraSize / 2,
        -cameraSize / 2,
        1,
        1000
      );
      // Hide objects intended for first person camera only
      miniMapCamera.layers.enableAll();
      miniMapCamera.layers.disable(LAYERS.FIRST_PERSON_ONLY);
      miniMapCamera.position.set(0, 100, 0);
      miniMapCamera.lookAt(0, 0, 0);
    },

    // TODO: Put init and animate methods at top of list
    animate: function () {
      // if (this.gameStatus === GAME_STATUS.ENDED) {
      //   return;
      // }
      const delta = clock.getDelta();
      // update camera controls
      controls.update(delta);

      if (this.gameStatus !== GAME_STATUS.PAUSED) {
        // Update Birds
        birdMapToArray(birds).forEach((bird) => bird.update(delta));
      }
      // render scene for main camera
      renderer.setViewport(0, 0, window.innerWidth, window.innerHeight);
      renderer.render(scene, camera);

      // render minimap camera only if not in photography mode
      if (!this.photographyViewEnabled()) {
        renderer.setScissorTest(true);
        const miniMapX = window.innerWidth - insetWidth - 16;
        // Y position greater to account for overlaid header element
        const miniMapY = window.innerHeight - insetHeight - 70;
        renderer.setScissor(miniMapX, miniMapY, insetWidth, insetHeight);
        renderer.setViewport(miniMapX, miniMapY, insetWidth, insetHeight);

        renderer.render(scene, miniMapCamera);
        renderer.setScissorTest(false);
      }

      // Update Performance Stats if in debug mode
      this.isDebugMode && stats.update();

      // Update Water
      water.material.uniforms["time"].value += delta * WATER_SPEED;

      animationID = requestAnimationFrame(this.animate);
    },
    updateDimensions: function () {
      // TODO: Repair this function and css that affects it
      // Bind renderer size to canvas
      // Source: https://r105.threejsfundamentals.org/threejs/lessons/threejs-responsive.html
      let { width, height, clientWidth, clientHeight } = renderer.domElement;
      let needsResize = width !== clientWidth || height !== clientHeight;

      if (!needsResize) {
        return;
      }

      renderer.setSize(clientWidth, clientHeight, false);
      //  Update camera aspect
      camera.aspect = clientWidth / clientHeight;
      camera.updateProjectionMatrix();

      // For MiniMap Camera
      // aspect is always 1:1, size relative to window width
      //TODO: resolve minimap position issues at window widths < 470 px
      insetWidth = clientWidth / 6;
      insetHeight = insetWidth;
    },
    init: async function () {
      // TODO: Need for Loading Manager?
      scene = new THREE.Scene();
      clock = new THREE.Clock();

      const canvas = this.$refs["threejs-canvas"];
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setClearColor(0xff0000, 1);

      // Add performance stats if in debug mode
      if (this.isDebugMode) {
        stats = new Stats();
        document.body.appendChild(stats.dom);
      }
      // Load Audio
      shutterSound = new Audio(require("@/assets/audio/cameraShutter.mp3"));

      // Initialize Cameras
      this.initCameras();

      this.initSky();
      scene.fog = new THREE.FogExp2(0xa3a3a3, 0.01);

      // Lights
      const ambLight = new THREE.AmbientLight(0xffffff, 1.1);
      const dirLight = new THREE.DirectionalLight(0xf9dede, 0.4);

      dirLight.position.x = 50;
      dirLight.position.y = 100;
      dirLight.position.z = 50;

      // Shadow Settings
      configureRendererShadowSettings(renderer);
      configureLightShadowSettings(dirLight);

      scene.add(dirLight);
      scene.add(ambLight);

      // Load Models
      const gltfLoader = new GLTFLoader();

      // Moving Water
      const waterNormals = new THREE.TextureLoader().load(
        require("@/assets/waternormals.jpg")
      );
      waterNormals.wrapS = waterNormals.wrapT = THREE.RepeatWrapping;

      const waterGeometry = new THREE.PlaneGeometry(350, 350);

      water = new Water(waterGeometry, {
        waterColor: "teal",
        sunColor: "gold",
        textureWidth: 512,
        textureHeight: 512,
        waterNormals: waterNormals,
        distortionScale: 3.7,
        fog: scene.fog !== undefined,
        sunDirection: dirLight.position.clone().normalize()
      });
      water.position.y =
        this.environment === ENVIRONMENTS.FOREST
          ? RENDER_CONSTANTS.FOREST_WATER_Y_POSITION
          : RENDER_CONSTANTS.WETLAND_WATER_Y_POSITION;
      water.rotation.x = -Math.PI / 2;
      // water shader should not affect the mini map
      setLayer(water, LAYERS.FIRST_PERSON_ONLY);
      scene.add(water);

      // Draco loader for compressed gltf files
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath("loaders/draco/");
      gltfLoader.setDRACOLoader(dracoLoader);

      function loadModel(url) {
        return new Promise((resolve, reject) => {
          gltfLoader.load(url, resolve, null, reject);
        });
      }

      // Load Models
      let promises = [];

      // Load Birds
      const environmentConfig =
        this.environment === "forest" ? forestJSON : wetlandJSON;
      promises = promises.concat(
        environmentConfig.map((birdConfig) => {
          Bird.createInstances(birdConfig, loadModel).then((birdList) => {
            birdList &&
              birdList.forEach((bird) => {
                setLayer(bird.model, LAYERS.FIRST_PERSON_ONLY);
                setCastShadow(bird.model, true);
              });
            birds[birdConfig.commonName] = birdList;
          });
        })
      );

      await Promise.all(promises);

      // TODO: Consider storing this logic in an external module
      await loadModel(
        this.getModelPath(`landscapes/${this.environment}`, "glb")
      ).then((gltf) => {
        // Promise debugging log
        // console.log("landscape model .then() entered");
        models[this.environment] = gltf.scene;
        setLayer(models[this.environment], LAYERS.TERRAIN);
        colliders = processTerrainModel(gltf, scene);
        // Remove grasses from colliders
        const colliderGrasses = [];
        const colliderTrees = [];
        colliders.traverse((child) => {
          if (isGrass(child)) {
            colliderGrasses.push(child);
          } else if (isTree(child)) {
            colliderTrees.push(child);
          }
        });
        colliderGrasses.forEach((grass) => {
          grass.removeFromParent();
        });
        colliderTrees.forEach((tree) => {
          tree.removeFromParent();
        });
      });

      // Once all models have been loaded
      // Promise debugging log
      // console.log("Gamescene Promise.all().then() entered");
      scene.add(models[this.environment]);
      birdMapToArray(birds).forEach((bird) => {
        scene.add(bird.model);
        scene.add(bird.marker);
      });
      // Set spawn points for birds
      if (TEST_PLACEMENT) {
        this.setTestSpawnPoints();
      } else {
        this.setSpawnPoints(models[this.environment]);
      }
      // Controls
      this.initCameraControls(canvas);

      // Choose identification objective
      this.selectNewIdentificationObjective();
      // update initial canvas dimensions
      this.updateDimensions();
      this.animate();
      this.$emit("modelsLoaded");
    },
    setTestSpawnPoints: function () {
      const rotation = new THREE.Euler(0, 0, Math.PI);
      const distanceBetweenModels = 1;

      birdMapToArray(birds).forEach((bird, index, array) => {
        const startingXCoordinate = (-array.length * distanceBetweenModels) / 2;

        // position birds in la line along the x axis in front of the camera
        bird.setPosition(
          {
            x: startingXCoordinate + index * distanceBetweenModels,
            y: 0,
            z: 5
          },
          rotation
        );
      });
    },
    setSpawnPoints: function (terrainModel) {
      // Replace spaces with underscores
      // const replaceSpaces = (str) => str.replace(/\s/g, "_");
      const birdInstances = birdMapToArray(birds);
      const findInstanceIndex = (spawnPoint, positionType) =>
        birdInstances.findIndex(
          // replace underscores with spaces
          (bird) => {
            return (
              spawnPoint.name.includes(bird.config.filename) &&
              bird.positionType === positionType
            );
          }
        );

      // TODO: get spawn points from terrain model processing function, rather than traversing the model again
      terrainModel.traverse((child) => {
        if (child.name === "spawnPoints") {
          // Traverse all spawn points
          child.traverse((spawnPoint) => {
            let positionType;
            // TODO: make sure this is working
            if (spawnPoint.name.includes("inFlight")) {
              positionType = BIRD_POSITION_TYPES.FLYING;
            } else {
              positionType = BIRD_POSITION_TYPES.PERCHED;
            }
            // Finds the first index of birdInstances where the bird name is included in the spawn point
            const index = findInstanceIndex(spawnPoint, positionType);
            // If object in terrain has a corresponding bird instance
            if (index > -1) {
              // Remove bird from array so we can track which birds have had their spawn points set
              const bird = birdInstances.splice(index, 1)[0];
              if (positionType === BIRD_POSITION_TYPES.GROUND) {
                bird.setPosition(spawnPoint.position, spawnPoint.rotation);
                let instanceIndex = findInstanceIndex(spawnPoint, positionType);
                while (instanceIndex > -1) {
                  const birdInstance = birdInstances.splice(
                    instanceIndex,
                    1
                  )[0];
                  // birdInstance.setPositionRandomized(spawnPoint.position);
                  birdInstance.setPosition(
                    spawnPoint.position,
                    spawnPoint.rotation
                  );
                  instanceIndex = findInstanceIndex(spawnPoint, positionType);
                }
              } else {
                // Tree birds should spawn in the tree
                bird.setPosition(spawnPoint.position, spawnPoint.rotation);
                // Promise debugging log
                // console.log(
                //   `Bird ${bird.config.commonName} positioned at ${spawnPoint.position.x}, ${spawnPoint.position.y}, ${spawnPoint.position.z} `
                // );
              }
            }
          });
        }
      });
      // Debugging-remove before merging branch: log birds that have not been positioned
      birdInstances.forEach((bird) => {
        const origin = new THREE.Vector3(0, 0, 0);
        // if bird positioned at origin, log
        if (bird.model.position.equals(origin)) {
          console.warn(
            `Bird ${bird.config.commonName} has not been positioned`
          );
        }
      });
    },
    selectNewIdentificationObjective: function () {
      if (this.identificationObjective) {
        // Add bird to list of identified birds
        identifiedBirds.push(this.identificationObjective.config.commonName);
        // Turn off marker for previous objective
        this.identificationObjective.toggleMarker();
      }

      let unselectedBirds = birdMapToArray(birds).filter(
        (bird) => !identifiedBirds.includes(bird.config.commonName)
      );

      if (unselectedBirds.length === 0 && this.identificationObjective) {
        this.addMessage("Congratulations! You found all the birds!");
        this.showEndGameMessage = true;
        this.unlockControls();
        controls.unlock();

        // Dispose of listeners and animation loop
        window.removeEventListener("resize", this.updateDimensions);
        window.removeEventListener("keydown", this.onKeyDown);
        window.removeEventListener("click", this.handleClickEvent);

        return;
      }
      this.identificationObjective = unselectedBirds
        ? unselectedBirds[Math.floor(Math.random() * unselectedBirds.length)]
        : birds[0];

      // Turn on marker for current objective
      this.identificationObjective?.toggleMarker();
    },

    initSky() {
      // TODO: convert cubetiles to jpg
      // TODO: load imgs through src dir, not public
      // TODO: Find or make a good environment panorama - maybe showing mountains in the distance
      scene.background = new THREE.CubeTextureLoader()
        .setPath(`cube/${this.environment}/`)
        .load([
          "px.jpeg",
          "nx.jpeg",
          "py.jpeg",
          "ny.jpeg",
          "pz.jpeg",
          "nz.jpeg"
        ]);
    },
    mobileCheck: function () {
      // if we want a more complete list use this: http://detectmobilebrowsers.com/
      const isMobile = /iphone|ipod|android|blackberry|fennec/.test(
        navigator.userAgent.toLowerCase()
      );
      return isMobile;
    },
    endGame() {
      this.cleanUpThreeJS();

      // route to the home page after 3 seconds
      this.$router.push({ path: "/" });
    },

    cleanUpThreeJS: function () {
      this.identifiedBirds = [];

      // End all bird audio
      birdMapToArray(birds).forEach((bird) => bird.endAudio());

      // Stop the animation loop
      if (animationID) {
        cancelAnimationFrame(animationID);
        animationID = null;
      }

      // Remove all children from the scene
      this.removeAllChildren(scene);

      // Dispose renderer
      if (renderer) {
        renderer.dispose();
      }

      // Dispose controls
      if (controls) {
        controls.dispose();
      }

      function clearObject(obj) {
        for (var key in obj) {
          delete obj[key];
        }
      }

      clearObject(birds);
      colliders = null;
      clearObject(models);
    },

    removeAllChildren: function (object) {
      while (object.children.length > 0) {
        this.removeAllChildren(object.children[0]);
        object.remove(object.children[0]);
      }

      if (object.geometry) {
        object.geometry.dispose();
      }

      if (object.material) {
        if (Array.isArray(object.material)) {
          object.material.forEach((material) => this.disposeMaterial(material));
        } else {
          this.disposeMaterial(object.material);
        }
      }

      if (object.texture) {
        object.texture.dispose();
      }
    },
    disposeMaterial: function (material) {
      if (material.map && !material.map.isTextureImmutable)
        material.map.dispose();
      if (material.lightMap && !material.lightMap.isTextureImmutable)
        material.lightMap.dispose();
      if (material.aoMap && !material.aoMap.isTextureImmutable)
        material.aoMap.dispose();
      if (material.emissiveMap && !material.emissiveMap.isTextureImmutable)
        material.emissiveMap.dispose();
      if (material.bumpMap && !material.bumpMap.isTextureImmutable)
        material.bumpMap.dispose();
      if (material.normalMap && !material.normalMap.isTextureImmutable)
        material.normalMap.dispose();
      if (material.specularMap && !material.specularMap.isTextureImmutable)
        material.specularMap.dispose();
      if (material.envMap && !material.envMap.isTextureImmutable)
        material.envMap.dispose();
      material.dispose();
    }
  }
};
</script>

<style scoped>
/* Setting opacity at the background color, rather than with opacity-50 bootstrap class */
/* https://stackoverflow.com/a/5770362 */
.bg-black-opacity-50 {
  background-color: rgba(0, 0, 0, 0.5);
}
.bg-black-opacity-75 {
  background-color: rgba(0, 0, 0, 0.75);
}
.showAboveCanvas {
  z-index: 1;
}
.cursorPointer {
  cursor: pointer;
}
#reticleContainer {
  position: relative;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  /* View a gradient in background when viewing through the camera */
  box-shadow: 0px 0px 100px #000000 inset;
}
#reticleSVG {
  width: 100%;
  max-height: 90vh;
}

@keyframes shutterAnimation {
  0% {
    background: rgba(0, 0, 0, 0);
  }
  50% {
    background: rgba(0, 0, 0, 1);
  }
  100% {
    background: rgba(0, 0, 0, 0);
  }
}

.animateShutter {
  animation: shutterAnimation 0.2s ease-in-out forwards;
}
</style>
